/**
 * Report a search once its results have settled, rather than once per keystroke.
 *
 * `fetching` going true→false is what marks a term as answered, so the caller passes the read's
 * own loading flag and the (already debounced) keyword behind it. A term is counted once while it
 * stands — a second read on the same term (another page, a load-more) does not re-count it — and
 * clearing the keyword forgets it, so searching for it again counts again. An empty term is never
 * counted, and nothing is reported on the server. A read that failed (`error`) is not counted, so
 * the retry that answers it is. A term whose results are already loaded when this starts
 * (server-rendered, or a refresh, a shared link or Back) was searched on an earlier visit, so the
 * next reload on it (a filter or chip change) does not count it again.
 */
export const useSettledSearchTracking = (
  fetching: Ref<boolean>,
  keyword: Ref<string>,
  onSettled: (term: string) => void,
  { error }: { error?: Ref<unknown> } = {},
) => {
  let lastTracked = fetching.value ? '' : keyword.value.trim();

  watch(fetching, (isFetching, wasFetching) => {
    if (import.meta.server || !wasFetching || isFetching) {
      return;
    }

    const term = keyword.value.trim();
    if (!term) {
      lastTracked = '';
      return;
    }

    if (!error?.value && term !== lastTracked) {
      lastTracked = term;
      onSettled(term);
    }
  });
};
